import { useQuery } from '@apollo/client'
import { Nullable } from '@dai/common'
import { CheckCircle, Close, Info, WarningRounded } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import {
  DisplaySnapshotFragment,
  GetDisplaySnapshotsQuery,
  GetDisplaySnapshotsQueryVariables,
  Ordering,
} from 'graphql/generated-types'
import { GET_DISPLAY_SNAPSHOTS } from 'graphql/queries'
import { useCallback } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import AutoSizer from 'react-virtualized-auto-sizer'

import CenteredBox from '@/components/CenteredBox'
import useT from '@/i18n/useT'

type SnapshotListProps = {
  onClose: () => void
  setSnapshot: (snapshot: Nullable<DisplaySnapshotFragment>) => void
  snapshot: Nullable<DisplaySnapshotFragment>
  displayId: Nullable<string>
}

const LIMIT = 20

const SnapshotList: React.FC<SnapshotListProps> = ({
  onClose,
  setSnapshot,
  snapshot,
  displayId,
}) => {
  const { t } = useT()
  const variables: GetDisplaySnapshotsQueryVariables = {
    filters: {
      display: {
        id: {
          exact: displayId,
        },
      },
    },
    order: {
      created: Ordering.DESC,
    },
    pagination: {
      limit: LIMIT,
      offset: 0,
    },
  }

  const { data, fetchMore, loading, error } = useQuery<
    GetDisplaySnapshotsQuery,
    GetDisplaySnapshotsQueryVariables
  >(GET_DISPLAY_SNAPSHOTS, {
    variables,
    notifyOnNetworkStatusChange: true,
    skip: !displayId,
  })

  const handleNext = useCallback(() => {
    if (loading || !data?.tenantDisplaySnapshots?.results) return
    if (
      data.tenantDisplaySnapshots.results.length >=
      data.tenantDisplaySnapshots.totalCount
    )
      return

    fetchMore({
      variables: {
        ...variables,
        pagination: {
          limit: LIMIT,
          offset: data.tenantDisplaySnapshots.results.length,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.tenantDisplaySnapshots?.results) return prev
        return {
          ...prev,
          ...fetchMoreResult,
          tenantDisplaySnapshots: {
            ...prev.tenantDisplaySnapshots,
            ...fetchMoreResult.tenantDisplaySnapshots,
            results: [
              ...prev.tenantDisplaySnapshots.results,
              ...fetchMoreResult.tenantDisplaySnapshots.results,
            ],
          },
        }
      },
    })
  }, [fetchMore, data, loading, variables])

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button sx={{ minWidth: 0, p: 1 }} onClick={() => onClose()}>
          <Close />
        </Button>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }} />
        <Info />
        <Typography variant="body1">
          {t('editPlanogram.snapshotList.description')}
        </Typography>
      </Box>

      <Box sx={{ height: '100%' }}>
        <AutoSizer>
          {({ height, width }) => (
            <InfiniteScroll
              scrollableTarget="list"
              next={handleNext}
              hasMore={
                (data?.tenantDisplaySnapshots.results.length || 0) <
                (data?.tenantDisplaySnapshots.totalCount || 0)
              }
              loader={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 2,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              }
              height={height}
              style={{
                width,
                border: '1px solid lightGrey',
                borderRadius: 8,
              }}
              dataLength={data?.tenantDisplaySnapshots.results.length || 0}
            >
              {error ? (
                <CenteredBox>
                  <Alert severity="error">{error.message}</Alert>
                </CenteredBox>
              ) : loading && !data?.tenantDisplaySnapshots.results.length ? (
                <CenteredBox style={{ height }}>
                  <CircularProgress size={24} />
                </CenteredBox>
              ) : !data?.tenantDisplaySnapshots.results.length ? (
                <CenteredBox>
                  <Alert severity="info">No snapshots found</Alert>
                </CenteredBox>
              ) : (
                <Box
                  p={2}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  {data?.tenantDisplaySnapshots.results.map(s => {
                    return (
                      <Stack
                        onClick={() => {
                          setSnapshot(s)
                        }}
                        key={s.id}
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{
                          p: 2,
                          flex: 1,
                          width: '100%',
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          {s.images?.[0]?.url ? (
                            <img
                              src={s.images[0].url}
                              alt={s.display.name}
                              style={{ height: '100%' }}
                            />
                          ) : (
                            <WarningRounded />
                          )}
                        </Box>
                        <Stack spacing={0.5} sx={{ flex: 1 }}>
                          {s.user?.firstName && s.user?.lastName && (
                            <Typography variant="body1">
                              {s.user.firstName} {s.user.lastName}
                            </Typography>
                          )}
                          {s.user?.email && (
                            <Typography variant="body1">
                              {s.user.email}
                            </Typography>
                          )}
                          <Typography variant="body1">
                            {dayjs(s.captureTime).fromNow()}
                          </Typography>
                        </Stack>
                        {snapshot?.id === s.id && (
                          <CheckCircle
                            color="primary"
                            sx={{ height: 30, width: 30 }}
                          />
                        )}
                      </Stack>
                    )
                  })}
                  {data?.tenantDisplaySnapshots.results.length >=
                    data?.tenantDisplaySnapshots.totalCount &&
                    !loading && (
                      <CenteredBox sx={{ p: 2 }}>
                        <Typography variant="h6">End of Results</Typography>
                      </CenteredBox>
                    )}
                </Box>
              )}
            </InfiniteScroll>
          )}
        </AutoSizer>
      </Box>
    </>
  )
}

export default SnapshotList
